import type {
  AvatarId,
  Equipment,
  LeaderboardMeResponse,
  LeaderboardResponse,
  LevelProof,
  Wallet,
} from "@nano/contracts";
import type { HouseId } from "@nano/game-config";

export const SAVE_VERSION = 2;

export type Stars = 1 | 2 | 3;

export interface LocalLevel {
  readonly bestStars: Stars;
  /** Fastest plausible completion in ms (for future time rankings). */
  readonly bestTimeMs?: number | null;
}

export interface Settings {
  readonly sound: boolean;
  readonly music: boolean;
  readonly vibration: boolean;
  /** Device pixel ratio cap for the canvas (1–2). */
  readonly maxDpr: number;
  /** In-level companion bust; missing means on (older saves). */
  readonly companion?: boolean;
}

export interface ServerState {
  readonly playerId: string;
  readonly publicTag: string;
  readonly wallet: Wallet;
  readonly owned: readonly string[];
  readonly hasRecoveryCode: boolean;
  /** "house:level" -> stars the server has verified. */
  readonly verified: Readonly<Record<string, Stars>>;
  /** Achievements whose gold the server has paid. */
  readonly claimedAchievements: readonly string[];
}

/** Device-side numbers for badges the server cannot verify. */
export interface LocalStats {
  /** Distinct local calendar days with at least one level played. */
  readonly playDayCount: number;
  readonly lastPlayDay: string | null;
  /** Longest run of correct placements without a mistake in one level. */
  readonly bestCombo: number;
}

export interface SaveData {
  readonly version: typeof SAVE_VERSION;
  readonly profile: {
    readonly guestId: string;
    readonly avatarId: AvatarId | null;
    readonly displayName: string | null;
    readonly createdAt: number;
    readonly seenTutorials: readonly string[];
    readonly cityHintDone: boolean;
    readonly profileDirty: boolean;
  };
  readonly session: { readonly token: string; readonly expiresAt: number } | null;
  /** Local progress (verified or waiting for sync). Never shrinks. */
  readonly progress: Readonly<Partial<Record<HouseId, Readonly<Record<number, LocalLevel>>>>>;
  readonly server: ServerState | null;
  readonly equipped: Equipment;
  readonly equipmentDirty: boolean;
  readonly settings: Settings;
  readonly leaderboard: {
    readonly top: LeaderboardResponse | null;
    readonly me: LeaderboardMeResponse | null;
    readonly fetchedAt: number;
  } | null;
  readonly syncMeta: { readonly lastSyncedAt: number | null; readonly sessionLost: boolean };
  readonly stats: LocalStats;
  /** Achievements whose unlock moment has already been shown. */
  readonly announcedAchievements: readonly string[];
  /** Shop item the player is saving up for (shown as a goal in the HUD). */
  readonly wishlist: string | null;
  readonly prefs: {
    /** Rank shown the last time the leaderboard was opened, for "up N places". */
    readonly lastRank: number | null;
    readonly installHintDismissedAt: number | null;
  };
}

export interface OutboxEntry {
  readonly id: number;
  readonly houseId: HouseId;
  readonly levelId: number;
  readonly contentVersion: string;
  readonly proof: LevelProof;
  readonly stars: Stars;
  readonly predictedGold: number;
  readonly createdAt: number;
  readonly status: "pending" | "rejected";
  readonly reason?: string;
}

export type NewOutboxEntry = Omit<OutboxEntry, "id">;
